import React from 'react';
import { CONTENT_SOURCE } from '@common/global-config';
import { agentHelper } from './AgentHelper';
import { increment } from '../Firebase';
import MessageRenderer from './MessageRenderer';
import { withStyles } from '@material-ui/core/styles';
import {
    Card,
    TextField,
    Typography,
    Paper,
    IconButton,
    CircularProgress,
    Button
} from '@material-ui/core';
import {
    Close as CloseIcon
} from '@material-ui/icons';
import { ReactComponent as OskiAvatar } from '../../assets/avatar_default_state.svg';
import { ReactComponent as SendArrowIcon } from '../../assets/arrow.svg';
import { ReactComponent as ChatBubble } from '../../assets/chat-bubble.svg';
import { ThemeContext } from '../../config/config.js';
import { withResponsive } from '../../util/ResponsiveContext';
import { MOBILE_CHAT_SHEET_HEIGHT } from './MobileBottomSheet';
import MobileBottomSheet from './MobileBottomSheet';
import {
    MOBILE_AGENT_AVATAR_HEIGHT,
    MOBILE_AGENT_AVATAR_WIDTH,
    MOBILE_AGENT_FAB_BOTTOM,
    MOBILE_FAB_RIGHT,
    DESKTOP_TOOLTIP_RIGHT,
    PAGE_BG,
} from './mobileFabStyles';
import {
    getHelpPenaltyBadgeText,
    getChatPenaltyMode,
    getHintPenaltyMode,
    shouldJudgeAgentAnswerReveal,
    shouldPenalizeAgentOnOpen,
} from '../../util/helpPenaltyMode.js';
import { resolveChatModel } from '../../util/chatModel.js';
import { collectLessonTopics, isFullChatLesson, isOfficeHoursLesson, resolveFullChatPrompt } from '../../util/officeHours.js';
import { chooseVariables, variabilize } from '../../platform-logic/variabilize.js';
import { boundChatMessages, historyPageMessages } from '../../util/chatHistory.js';
import AnthropologyReviewStarters from './AnthropologyReviewStarters';

const CHAT_THEME = {
    primary: '#4c7d9f',
    primaryDark: '#3f7091',
    accent: '#ffc300',
    pale: '#a3c5de',
    surface: '#eef4fa',
};

const VISION_IMAGE_MIME_TYPES = new Set([
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/webp',
]);

function isVisionSafeImageDataUrl(dataUrl) {
    return typeof dataUrl === 'string'
        && /^data:image\/(png|jpe?g|gif|webp);base64,/i.test(dataUrl);
}

// Tail tip x=382 in the 520-wide chat-bubble viewBox
const LAUNCHER_TAIL_CENTER_PERCENT = (382 / 520) * 100;

// Shown when the stream completes with no text. Never sent back as history.
const EMPTY_RESPONSE_FALLBACK = "Sorry, I didn't get a response. Please try again.";

const FALLBACK_SUGGESTED_QUESTIONS = [
    'What should I try first?',
    'Can you explain this step in simpler words?',
    'Why might my answer be wrong?',
];

const styles = (theme) => ({
    chatContainer: {
        position: 'fixed',
        bottom: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        zIndex: 1000,
        borderRadius: 12,
        overflow: 'hidden',
        minWidth: 300,
        minHeight: 400,
        maxWidth: '95vw',
        maxHeight: '90vh',
        fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    },
    chatContainerSheet: {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: MOBILE_CHAT_SHEET_HEIGHT,
        maxWidth: '100vw',
        maxHeight: MOBILE_CHAT_SHEET_HEIGHT,
        minWidth: 0,
        minHeight: 0,
        borderRadius: '20px 20px 0 0',
        zIndex: 1300,
        boxShadow: '0 -10px 40px rgba(16, 24, 40, 0.16)',
    },
    launcherAvatarMobile: {
        width: MOBILE_AGENT_AVATAR_WIDTH,
        height: MOBILE_AGENT_AVATAR_HEIGHT,
        display: 'block',
        filter: 'drop-shadow(0 4px 14px rgba(76, 125, 159, 0.32))',
    },
    chatHeader: {
        background: `linear-gradient(135deg, ${CHAT_THEME.primary} 0%, ${CHAT_THEME.primaryDark} 100%)`,
        color: 'white',
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `3px solid ${CHAT_THEME.accent}`,
    },
    chatTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontWeight: 600
    },
    chatMessages: {
        flex: 1,
        overflowY: 'auto',
        padding: '20px 24px',
        backgroundColor: CHAT_THEME.surface,
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    message: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        width: '100%'
    },
    userMessage: {
        alignItems: 'flex-end'
    },
    assistantMessage: {
        alignItems: 'stretch',
        marginTop: 8
    },
    messageBubble: {
        padding: '10px 14px',
        borderRadius: 16,
        maxWidth: '75%',
        wordWrap: 'break-word'
    },
    userBubble: {
        backgroundColor: CHAT_THEME.primary,
        color: 'white'
    },
    assistantBubble: {
        backgroundColor: 'white',
        color: '#333'
    },
    assistantContent: {
        width: '100%',
        maxWidth: '100%',
        wordWrap: 'break-word'
    },
    messageMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        paddingLeft: 8,
        paddingRight: 8
    },
    chatInput: {
        padding: 16,
        backgroundColor: 'white',
        borderTop: `1px solid ${CHAT_THEME.pale}`,
    },
    suggestions: {
        marginTop: 4,
        padding: '12px 14px',
        borderRadius: 14,
        backgroundColor: 'rgba(255, 255, 255, 0.72)',
        border: `1px solid ${CHAT_THEME.pale}`,
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    suggestionsTitle: {
        color: '#5f6f7f',
        fontSize: 13,
        fontWeight: 700,
        marginBottom: 8,
    },
    suggestionList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    suggestionChip: {
        border: `1px solid ${CHAT_THEME.pale}`,
        backgroundColor: '#f7fbfe',
        color: CHAT_THEME.primaryDark,
        borderRadius: 999,
        padding: '7px 11px',
        fontSize: 13,
        fontWeight: 700,
        lineHeight: 1.25,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 0.16s ease',
        '&:hover': {
            borderColor: CHAT_THEME.primary,
            backgroundColor: CHAT_THEME.surface,
        },
        '&:disabled': {
            cursor: 'default',
            opacity: 0.65,
        },
    },
    inputContainer: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
    },
    messageInput: {
        flex: 1,
        '& .MuiOutlinedInput-root': {
            borderRadius: 20
        }
    },
    sendButton: {
        padding: 0,
        minWidth: 0,
        width: 48,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        backgroundColor: 'transparent !important',
        border: 'none',
        boxShadow: 'none',
        borderRadius: 0,
        flexShrink: 0,
        '&:hover': {
            backgroundColor: 'transparent !important',
            boxShadow: 'none',
            '& $sendIcon': {
                transform: 'scale(1.06)',
            },
        },
        '&:disabled': {
            backgroundColor: 'transparent !important',
            opacity: 0.45,
        },
    },
    sendIcon: {
        width: 48,
        height: 48,
        display: 'block',
        flexShrink: 0,
        transition: 'transform 0.2s ease',
    },
    floatingLauncher: {
        position: 'fixed',
        bottom: MOBILE_AGENT_FAB_BOTTOM,
        right: MOBILE_FAB_RIGHT,
        zIndex: 1001,
        maxWidth: 'calc(100vw - 40px)',
        transform: 'none',
        [theme.breakpoints.up('md')]: {
            right: DESKTOP_TOOLTIP_RIGHT,
        },
    },
    embeddedLauncher: {
        width: '100%',
        padding: '24px 16px',
        boxSizing: 'border-box',
    },
    launcherButton: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        '&:hover $launcherAvatar': {
            transform: 'scale(1.06)',
        },
    },
    launcherStack: {
        width: '100%',
        maxWidth: 300,
    },
    launcherBubbleWrap: {
        position: 'relative',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'visible',
        transition: 'max-height 0.25s ease, opacity 0.2s ease, margin-bottom 0.2s ease',
    },
    launcherBubbleWrapHidden: {
        maxHeight: 0,
        opacity: 0,
        marginBottom: 0,
        padding: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
    },
    launcherBubbleWrapVisible: {
        maxHeight: 200,
        opacity: 1,
        marginBottom: 0,
    },
    launcherBubbleShape: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'block',
        overflow: 'visible',
        pointerEvents: 'none',
        '& path': {
            fill: PAGE_BG,
            stroke: CHAT_THEME.primary,
            vectorEffect: 'non-scaling-stroke',
        },
    },
    launcherBubbleContent: {
        position: 'relative',
        padding: '14px 10px 32px',
        textAlign: 'left',
        boxSizing: 'border-box',
    },
    launcherTitle: {
        margin: 0,
        fontWeight: 700,
        fontSize: 15,
        lineHeight: 1.2,
        color: CHAT_THEME.primaryDark,
    },
    launcherDescription: {
        margin: '2px 0 0',
        fontSize: 12,
        lineHeight: 1.3,
        color: '#5c6b7a',
    },
    launcherPill: {
        display: 'inline-block',
        marginTop: 4,
        padding: '2px 7px',
        borderRadius: 9999,
        border: `1px solid ${CHAT_THEME.pale}`,
        backgroundColor: '#ffffff',
        color: CHAT_THEME.primaryDark,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.2,
    },
    launcherAvatarRow: {
        display: 'flex',
        justifyContent: 'flex-start',
        paddingLeft: `calc(${LAUNCHER_TAIL_CENTER_PERCENT}% - 40px)`,
        marginTop: 4,
    },
    launcherAvatar: {
        width: 80,
        height: 74,
        display: 'block',
        filter: 'drop-shadow(0 4px 14px rgba(76, 125, 159, 0.32))',
        transition: 'transform 0.2s ease',
    },
    avatarIcon: {
        width: 32,
        height: 32,
        display: 'block',
        flexShrink: 0,
    },
    resizeHandle: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: 20,
        height: 20,
        cursor: 'nwse-resize',
        zIndex: 10,
    },
    officeHoursRoot: {
        boxShadow: 'none !important',
        backgroundColor: 'transparent',
        borderRadius: '0 !important',
        overflow: 'hidden',
        minWidth: '0 !important',
        minHeight: '0 !important',
        maxWidth: 'none',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative !important',
        bottom: 'auto',
        right: 'auto',
        left: 'auto',
    },
    officeHoursScrollPane: {
        flex: 1,
        width: '100%',
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingBottom: 16,
        boxSizing: 'border-box',
    },
    officeHoursColumn: {
        maxWidth: 900,
        width: 'calc(100% - 48px)',
        margin: '0 auto',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        boxSizing: 'border-box',
        [theme.breakpoints.down('sm')]: {
            width: 'calc(100% - 32px)',
        },
    },
    officeHoursIntro: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '28px 8px 8px',
        position: 'static',
    },
    officeHoursIntroAvatar: {
        width: 44,
        height: 44,
        display: 'block',
        marginBottom: 6,
    },
    officeHoursIntroTitle: {
        margin: 0,
        fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        fontSize: 18,
        fontWeight: 700,
        color: CHAT_THEME.primaryDark,
        lineHeight: 1.25,
    },
    officeHoursIntroSubtitle: {
        margin: '4px 0 0',
        fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        fontSize: 14,
        fontWeight: 500,
        color: '#5f6f7f',
        lineHeight: 1.35,
    },
    officeHoursComposerColumn: {
        maxWidth: 900,
        width: 'calc(100% - 48px)',
        margin: '0 auto',
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        boxSizing: 'border-box',
        [theme.breakpoints.down('sm')]: {
            width: 'calc(100% - 32px)',
        },
    },
    officeHoursAssistantMessage: {
        alignItems: 'flex-start',
        width: '100%',
        marginTop: 0,
    },
    officeHoursAssistantRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 12,
        width: '85%',
        maxWidth: '85%',
        alignSelf: 'flex-start',
        boxSizing: 'border-box',
    },
    officeHoursAssistantAvatar: {
        width: 32,
        height: 32,
        flexShrink: 0,
        marginTop: 2,
    },
    officeHoursAssistantBody: {
        flex: '1 1 auto',
        minWidth: 0,
        width: '100%',
        maxWidth: 'none',
    },
    officeHoursAssistantLabel: {
        fontSize: 14,
        fontWeight: 700,
        color: CHAT_THEME.primaryDark,
        marginBottom: 4,
        lineHeight: 1.3,
    },
    officeHoursAssistantText: {
        fontSize: 15,
        lineHeight: 1.65,
        fontWeight: 400,
        color: '#1f2933',
        width: '100%',
        maxWidth: 'none',
        '& p': {
            maxWidth: 'none',
        },
    },
    officeHoursUserBubble: {
        backgroundColor: '#d7e8f4',
        color: '#1f2933',
        boxShadow: 'none',
        borderRadius: 18,
        maxWidth: '58%',
        width: 'fit-content',
        marginLeft: 'auto',
        padding: '10px 16px',
    },
    officeHoursInput: {
        flexShrink: 0,
        width: '100%',
        backgroundColor: '#eef4fa',
        borderTop: 'none',
        padding: '8px 0 20px',
        boxSizing: 'border-box',
    },
    officeHoursMessageInput: {
        '& .MuiOutlinedInput-root': {
            borderRadius: 24,
            backgroundColor: '#fff',
        },
    },
});

class AgentChatbox extends React.Component {
    static contextType = ThemeContext;

    constructor(props) {
        super(props);
        const startsVisible = props.mode === 'embedded' && props.defaultOpen !== false;
        this.state = {
            isVisible: startsVisible,
            messages: [],
            currentMessage: '',
            isTyping: false,
            isGenerating: false,
            chatWidth: 400,
            chatHeight: 600,
            isResizing: false,
            suggestedQuestions: [],
            isLoadingSuggestedQuestions: false,
            suggestionsCacheKey: '',
            hasChatBeenOpened: false,
            isLauncherHovered: false,
            firstChatActionRecorded: false, // true once firstActionType has been written
            hasOlderMessages: false,
            isLoadingHistory: false,
            browsingHistory: false,
            historyError: '',
        };
        this.messagesEndRef = React.createRef();
        this.chatContainerRef = React.createRef();
        this.activeRequestId = 0;
        // Last problemId whose context was already reflected in an LLM turn.
        // Used to inject a role:user switch notice when the ITS advances problems
        // without clearing the chat thread (lesson change still clears).
        this._lastPromptedProblemId = null;
    }

    getFirebase = () => this.context?.firebase;

    getSessionId = () => agentHelper.getSessionId();

    componentWillUnmount() {
        this._mounted = false;
        this.activeRequestId += 1;
        this.historyController?.abort();
        agentHelper.cancelMessage();
    }

    loadHistory = async (latest = false) => {
        if (this.state.isGenerating || this.state.isLoadingHistory) return;
        const sid = this.getSessionId();
        const firstSequence = this.state.messages.find((message) => message.sequence)?.sequence;
        this.historyController = new AbortController();
        this.setState({ isLoadingHistory: true, historyError: '' });
        try {
            const page = await agentHelper.fetchHistory(latest ? null : firstSequence, this.historyController.signal);
            if (!this._mounted || sid !== this.getSessionId()) return;
            const archived = historyPageMessages(page.turns);
            await new Promise((resolve) => this.setState((prev) => {
                const messages = latest ? boundChatMessages(archived) : boundChatMessages([
                    ...archived,
                    ...prev.messages.filter((message) => message.sequence >= firstSequence),
                ], 'oldest');
                return { messages, hasOlderMessages: page.hasMore, browsingHistory: !latest,
                    isLoadingHistory: false };
            }, resolve));
        } catch (error) {
            if (this._mounted && sid === this.getSessionId()) this.setState({ isLoadingHistory: false, historyError: error.message });
        }
    };

    componentDidMount() {
        this._mounted = true;
        agentHelper.initSessionIfNeeded();
        const fb = this.getFirebase();
        const sid = this.getSessionId();
        const lesson = this.props.lesson;
        if (fb?.logChatSession && sid && agentHelper.needsSessionMetaWrite()) {
            fb.logChatSession(
                sid,
                agentHelper.buildChatSessionCreatePayload({
                    sessionId: sid,
                    lesson,
                    oats_user_id: this.context?.userID || null,
                    lms_user_id: this.context?.user?.user_id || null,
                    course_id: this.context?.user?.course_id || null,
                    course_name: this.context?.user?.course_name || lesson?.courseName || null,
                    course_code: this.context?.user?.course_code || null,
                    semester: fb.addMetaData?.({})?.semester || null,
                    treatment: this.context?.getTreatment?.() ?? null,
                    siteVersion: fb.siteVersion || null,
                    siteCommitHash: process.env.REACT_APP_COMMIT_HASH || null,
                    hintPenaltyMode: getHintPenaltyMode(lesson),
                    chatPenaltyMode: this._getChatPenaltyMode(),
                })
            );
            agentHelper.markSessionMetaWritten();
        }
        if (this.props.mode === 'embedded' && this.state.isVisible) {
            this.setState((prev) => ({
                messages: prev.messages.length === 0 ? this.buildGreetingMessages() : prev.messages,
            }));
            if (fb?.logChatSession && sid) {
                fb.logChatSession(sid, { chatOpenCount: increment(1), lastActivityAt: Date.now() });
            }
        }
        this.fetchSuggestedQuestionsIfNeeded();
        this._notifyPromoEligibility();
        this.props.onPromoLayoutChange?.();
    }

    _getChatPenaltyMode = () =>
        this.props.chatPenaltyMode || getChatPenaltyMode(this.props.lesson);

    _isOfficeHours = () =>
        this.props.officeHours === true ||
        isFullChatLesson(this.props.lesson);

    _isMobileView = () => {
        if (this.props.responsive?.isMobile) {
            return true;
        }
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return false;
        }
        return window.matchMedia('(max-width: 959.95px)').matches;
    };

    _showSuggestedQuestions = () =>
        Boolean(this.props.showSuggestedQuestions) &&
        !this._isOfficeHours() &&
        !this._isMobileView();

    _maybePenalizeAgentOnFirstQuery = () => {
        if (this._isOfficeHours()) {
            return;
        }
        if (!shouldPenalizeAgentOnOpen({ mode: this._getChatPenaltyMode() })) {
            return;
        }
        this.props.applyHelpPenalty?.();
    };

    _resolveStepAnswers = (step) => {
        if (!step) return [];
        const chosenVars = chooseVariables(
            Object.assign(
                {},
                this.props.problemVars || {},
                this.props.problem?.variabilization || {},
                step.variabilization || {}
            ),
            this.props.seed
        );
        const raw = step.stepAnswer;
        const list = Array.isArray(raw) ? raw : raw != null && raw !== '' ? [raw] : [];
        return list
            .map((ans) => String(variabilize(ans, chosenVars) ?? ans))
            .filter(Boolean);
    };

    _maybeJudgeAgentAnswerReveal = async (assistantText) => {
        if (this._isOfficeHours()) {
            return;
        }
        if (!shouldJudgeAgentAnswerReveal({ mode: this._getChatPenaltyMode() })) {
            return;
        }
        const text = (assistantText || '').trim();
        if (!text) return;

        const active = this.props.getActiveStepData?.();
        const step = active?.step;
        const stepIndex = active?.stepIndex;
        const answers = this._resolveStepAnswers(step);
        if (!answers.length) return;

        try {
            const result = await agentHelper.judgeAnswerReveal({
                assistantMessage: text,
                stepAnswers: answers,
                problemContext: this.getProblemContext(),
                stepId: step?.id || null,
                chatPrompt: this.props.lesson?.chat_prompt || 'PROMPTv2b.txt',
                chatDisplayMode: this.props.lesson?.chat_display_mode ?? 'Off',
                chatPenaltyMode: this._getChatPenaltyMode(),
                chatModel: resolveChatModel(this.props.lesson),
                lessonId: this.props.lesson?.id || null,
                condition: this.props.condition,
            });
            if (result?.answerRevealed) {
                this.props.applyHelpPenalty?.(stepIndex);
            }
        } catch (err) {
            console.warn('Answer-reveal judge failed; skipping help penalty', err);
        }
    };

    _getPromoAllowed = () => {
        const isMobile = this.props.responsive?.isMobile ?? false;
        return (
            this.props.showLauncherBubble !== false &&
            !this.props.hintsOpen &&
            !isMobile
        );
    };

    _getPromoEligible = () => {
        if (!this._getPromoAllowed()) return false;
        return (
            !this.state.hasChatBeenOpened || this.state.isLauncherHovered
        );
    };

    _notifyPromoEligibility = () => {
        const meta = {
            allowed: this._getPromoAllowed(),
            hasBeenOpened: Boolean(this.state.hasChatBeenOpened),
            isHovering: Boolean(this.state.isLauncherHovered),
        };
        const prev = this._lastReportedPromoMeta;
        if (
            prev &&
            prev.allowed === meta.allowed &&
            prev.hasBeenOpened === meta.hasBeenOpened &&
            prev.isHovering === meta.isHovering
        ) {
            return;
        }
        this._lastReportedPromoMeta = meta;
        this.props.onPromoEligibilityChange?.(meta);
    };

    componentDidUpdate(prevProps, prevState) {
        this._notifyPromoEligibility();
        const currentLessonID = this.props.lesson?.id;
        const prevLessonID = prevProps.lesson?.id;

        if (
            this.props.closeRequest != null &&
            this.props.closeRequest !== prevProps.closeRequest &&
            this.state.isVisible
        ) {
            this.setState({ isVisible: false });
        }

        // Only reset chat when the lesson changes — not when advancing to the next problem.
        if (currentLessonID && prevLessonID && currentLessonID !== prevLessonID) {
            this.clearConversation();
        }
        
        // Scroll to latest when chat is reopened, or when a new message is added
        if (!prevState.isVisible && this.state.isVisible) {
            // Double rAF: wait until remounted chat has finished layout
            requestAnimationFrame(() => {
                requestAnimationFrame(() => this.scrollToBottom(true));
            });
        } else if (!this.state.browsingHistory && this.state.messages.length > prevState.messages.length) {
            // New user/assistant turn — always stick to the latest message
            this.scrollToBottom(true);
        }

        if (this._showSuggestedQuestions()) {
            this.fetchSuggestedQuestionsIfNeeded();
        }
    }
    
    scrollToBottom = (force = false) => {
        if (!this.messagesEndRef.current) {
            return;
        }
        let messagesContainer = this.messagesEndRef.current.parentElement;
        while (messagesContainer && messagesContainer !== document.body) {
            const overflowY = window.getComputedStyle(messagesContainer).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
                break;
            }
            messagesContainer = messagesContainer.parentElement;
        }
        if (!messagesContainer) {
            return;
        }

        const distanceFromBottom =
            messagesContainer.scrollHeight -
            messagesContainer.scrollTop -
            messagesContainer.clientHeight;
        const isNearBottom = distanceFromBottom < 120;

        // Scroll only the messages pane (not scrollIntoView — that moves page/flex ancestors).
        if (force || isNearBottom) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
    };

    // Build a one-shot greeting from the current problem context. Returned as
    // an array so callers can splice it into `messages`. Kept agent-free
    // (purely client-side, no LLM call) — the goal is just to invite the
    // student to drive the conversation per the open-inquiry research finding,
    // not to make a contextual diagnosis.
    buildGreetingMessages = () => {
        const course = this.props.lesson?.courseName || 'this course';
        const lessonLabel = String(this.props.lesson?.name || '').replace(/##/g, '').trim();
        const fullChatScope = isOfficeHoursLesson(this.props.lesson)
            ? course
            : (lessonLabel || this.props.lesson?.topics || course);
        const subject = this.props.problem?.title ? `**${this.props.problem.title}**` : 'this problem';
        const customGreeting = String(this.props.lesson?.office_hours_greeting || '').trim();
        const greeting = this._isOfficeHours() && customGreeting
            ? customGreeting
            : this._isOfficeHours()
            ? [
                `Hi! I’m Oski. I’m here to help with questions about ${fullChatScope}.`,
                'You can ask me to explain a concept, work through something you’re stuck on, show you an example.',
                'What would you like to talk about?',
            ].join('\n\n')
            : `Hello! I'm Oski, your AI tutor. I'm here to think through ${subject} with you — feel free to ask me anything, or tell me where you're stuck.`;
        const fb = this.getFirebase();
        const sid = this.getSessionId();
        if (fb?.logChatSession && sid) {
            fb.logChatSession(sid, { greetingShown: true, lastActivityAt: Date.now() });
        }
        return [{
            id: `greeting-${Date.now()}`,
            role: 'assistant',
            content: greeting,
            timestamp: Date.now(),
            isGenerating: false,
        }];
    };

    getSuggestionsCacheKey = () => {
        const problemContext = this.getProblemContext();
        const studentState = this.getStudentState();

        return [
            problemContext.problemID || '',
            problemContext.currentStep?.id || '',
            studentState.isCorrect === undefined ? 'unknown' : String(studentState.isCorrect),
        ].join('::');
    };

    fetchSuggestedQuestionsIfNeeded = async () => {
        if (this._isOfficeHours()) {
            return;
        }
        if (!this._showSuggestedQuestions() || this.state.isLoadingSuggestedQuestions) {
            return;
        }
        if (this.props.allowEmbeddedClose && !this.state.isVisible) {
            return;
        }

        const cacheKey = this.getSuggestionsCacheKey();
        if (!cacheKey || cacheKey === this.state.suggestionsCacheKey) {
            return;
        }

        this.setState({
            isLoadingSuggestedQuestions: true,
            suggestionsCacheKey: cacheKey,
        });

        try {
            const problemContext = this.getProblemContext();
            const studentState = this.getStudentState();
            const { text } = this.extractConceptExplorationInput('', problemContext);
            const questions = await agentHelper.fetchSuggestedQuestions(
                problemContext,
                studentState,
                {
                    text,
                    images: [],
                    condition: this.props.condition,
                    lessonId: this.props.lesson?.id,
                },
                this.props.lesson?.chat_prompt || 'PROMPTv2b.txt',
                this.props.lesson?.chat_display_mode ?? 'Off',
                this._getChatPenaltyMode(),
                resolveChatModel(this.props.lesson),
            );

            this.setState({
                suggestedQuestions: questions.slice(0, 3),
                isLoadingSuggestedQuestions: false,
            });
        } catch (_error) {
            this.setState({
                suggestedQuestions: FALLBACK_SUGGESTED_QUESTIONS,
                isLoadingSuggestedQuestions: false,
            });
        }
    };

    handleLauncherPointerIn = () => {
        this.setState({ isLauncherHovered: true }, () => {
            this.props.onPromoLayoutChange?.();
        });
    };

    handleLauncherPointerOut = () => {
        this.setState({ isLauncherHovered: false }, () => {
            this.props.onPromoLayoutChange?.();
        });
    };

    handleLauncherKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.toggleChat();
        }
    };

    renderLauncher = () => {
        const { classes, responsive } = this.props;
        const isMobile = responsive?.isMobile ?? false;
        const mode = this.props.mode || 'floating';
        const launcherPlacement = this.props.closedLauncherPlacement || mode;
        const hintsOpen = this.props.hintsOpen;
        const hidePromoDueToOverlap = Boolean(this.props.hidePromoDueToOverlap);
        const promoEligible = this._getPromoEligible();
        const promoVisible = promoEligible && !hidePromoDueToOverlap;
        const bubbleWrapClass = promoVisible
            ? classes.launcherBubbleWrapVisible
            : classes.launcherBubbleWrapHidden;

        if (isMobile && launcherPlacement === 'floating') {
            if (hintsOpen || this.props.drawerOpen || this.props.mathKeyboardOpen) {
                return null;
            }

            return (
                <button
                    type="button"
                    className={`${classes.launcherButton} ${classes.floatingLauncher}`}
                    onClick={this.toggleChat}
                    onKeyDown={this.handleLauncherKeyDown}
                    aria-label="Open AI Tutor"
                >
                    <OskiAvatar className={classes.launcherAvatarMobile} aria-hidden="true" />
                </button>
            );
        }

        return (
            <button
                type="button"
                className={`${classes.launcherButton} ${
                    launcherPlacement === 'floating' ? classes.floatingLauncher : classes.embeddedLauncher
                }`}
                onClick={this.toggleChat}
                onKeyDown={this.handleLauncherKeyDown}
                onMouseEnter={this.handleLauncherPointerIn}
                onMouseLeave={this.handleLauncherPointerOut}
                onFocus={this.handleLauncherPointerIn}
                onBlur={this.handleLauncherPointerOut}
                aria-label="Open AI Tutor"
            >
                <div className={classes.launcherStack}>
                    <div
                        className={`${classes.launcherBubbleWrap} ${bubbleWrapClass}`}
                        aria-hidden={!promoVisible}
                    >
                        <ChatBubble
                            className={classes.launcherBubbleShape}
                            preserveAspectRatio="none"
                            aria-hidden="true"
                        />
                        <div className={classes.launcherBubbleContent}>
                            <p className={classes.launcherTitle}>AI Tutor</p>
                            <p className={classes.launcherDescription}>
                                Ask me any question about this problem or topic.
                            </p>
            <span className={classes.launcherPill}>
                                {getHelpPenaltyBadgeText(this._getChatPenaltyMode(), 'agent')}
                            </span>
                        </div>
                    </div>
                    <div className={classes.launcherAvatarRow}>
                        <OskiAvatar className={classes.launcherAvatar} aria-hidden="true" />
                    </div>
                </div>
            </button>
        );
    };

    toggleChat = () => {
        const fb = this.getFirebase();
        const sid = this.getSessionId();
        let nextVisible = false;

        this.setState(prevState => {
            const opening = !prevState.isVisible;
            nextVisible = opening;
            const needsGreeting = opening && prevState.messages.length === 0;

            if (fb?.logChatSession && sid) {
                if (opening) {
                    const delta = { chatOpenCount: increment(1), lastActivityAt: Date.now() };
                    // Record firstActionType = 'chat' only once per session
                    if (!prevState.firstChatActionRecorded) {
                        delta.firstActionType = 'chat';
                        delta.firstActionTimestampMs = Date.now();
                    }
                    fb.logChatSession(sid, delta);
                } else {
                    fb.logChatSession(sid, { chatCloseCount: increment(1), lastActivityAt: Date.now() });
                }
            }

            return {
                isVisible: opening,
                hasChatBeenOpened:
                    prevState.hasChatBeenOpened || opening || prevState.isVisible,
                firstChatActionRecorded: prevState.firstChatActionRecorded || opening,
                isLauncherHovered: opening ? false : prevState.isLauncherHovered,
                messages: needsGreeting ? this.buildGreetingMessages() : prevState.messages,
            };
        }, () => {
            if (this.props.onChatVisibilityChange) {
                this.props.onChatVisibilityChange(nextVisible);
            }
            this.props.onPromoLayoutChange?.();
        });
    };

    clearConversation = () => {
        this.historyController?.abort();
        this.activeRequestId += 1;
        const fb = this.getFirebase();
        const oldSid = this.getSessionId();
        // Attribute clear to the session that was active, then start a fresh one.
        if (fb?.logChatSession && oldSid) {
            fb.logChatSession(oldSid, { clearedCount: increment(1), lastActivityAt: Date.now() });
        }
        agentHelper.initializeSession();
        // Problem will not remount, so write create metadata for the new session here.
        const sid = this.getSessionId();
        const lesson = this.props.lesson;
        if (fb?.logChatSession && sid && agentHelper.needsSessionMetaWrite()) {
            fb.logChatSession(
                sid,
                agentHelper.buildChatSessionCreatePayload({
                    sessionId: sid,
                    lesson,
                    oats_user_id: this.context?.userID || null,
                    lms_user_id: this.context?.user?.user_id || null,
                    course_id: this.context?.user?.course_id || null,
                    course_name: this.context?.user?.course_name || lesson?.courseName || null,
                    course_code: this.context?.user?.course_code || null,
                    semester: fb.addMetaData?.({})?.semester || null,
                    treatment: this.context?.getTreatment?.() ?? null,
                    siteVersion: fb.siteVersion || null,
                    siteCommitHash: process.env.REACT_APP_COMMIT_HASH || null,
                    hintPenaltyMode: getHintPenaltyMode(lesson),
                    chatPenaltyMode: this._getChatPenaltyMode(),
                })
            );
            agentHelper.markSessionMetaWritten();
        }
        this.setState({
            messages: this.buildGreetingMessages(),
            suggestedQuestions: [],
            suggestionsCacheKey: '',
            firstChatActionRecorded: false,
            isGenerating: false,
            isTyping: false,
            currentMessage: '',
            hasOlderMessages: false,
            isLoadingHistory: false,
            browsingHistory: false,
            historyError: '',
        }, this.fetchSuggestedQuestionsIfNeeded);
        this._lastPromptedProblemId = null;
    };

    handleResizeStart = (event) => {
        event.preventDefault();
        this.setState({ isResizing: true });
        
        const startX = event.clientX;
        const startY = event.clientY;
        const startWidth = this.state.chatWidth;
        const startHeight = this.state.chatHeight;
        const maxWidth = window.innerWidth * 0.95;
        const minWidth = Math.min(300, maxWidth);

        const handleMouseMove = (e) => {
            const deltaX = startX - e.clientX; // Reverse for left-side resize
            const deltaY = startY - e.clientY; // Reverse for top-side resize
            
            const newWidth = Math.max(minWidth, Math.min(startWidth + deltaX, maxWidth));
            const newHeight = Math.max(400, Math.min(startHeight + deltaY, window.innerHeight * 0.9));
            
            this.setState({
                chatWidth: newWidth,
                chatHeight: newHeight
            });
        };

        const handleMouseUp = () => {
            this.setState({ isResizing: false });
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    handleInputChange = (event) => {
        this.setState({ currentMessage: event.target.value });
    };

    handleKeyPress = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.handleSendMessage();
        }
    };

    handleSendMessage = async (messageOverride = null) => {
        const { currentMessage } = this.state;
        const nextMessage = typeof messageOverride === 'string' ? messageOverride : currentMessage;
        
        if (!nextMessage.trim() || this.state.isGenerating) {
            if (typeof messageOverride === 'string' && this.state.isGenerating) {
                this.setState({ currentMessage: messageOverride });
            }
            return;
        }

        if (this.state.isLoadingHistory) return;
        if (new TextEncoder().encode(nextMessage).length > 16_000) {
            this.setState({ historyError: 'This message is too long. Please split it into smaller messages.' });
            return;
        }
        if (this.state.browsingHistory) {
            await this.loadHistory(true);
            if (this.state.browsingHistory) return;
        }

        const userMessage = nextMessage.trim();
        const messageId = Date.now();
        const requestId = this.activeRequestId + 1;
        this.activeRequestId = requestId;

        // OnOpen: penalize only when the student actually sends a query.
        this._maybePenalizeAgentOnFirstQuery();

        // Snapshot prior turns before we append this turn's placeholders.
        // This is a copy for the LLM payload — UI state is updated separately below.
        // Skip error bubbles, in-flight placeholders, and the empty-response
        // fallback: none of them are words Oski actually said.
        const conversationHistory = (this.state.messages || [])
            .filter((msg) => msg?.role === 'user' || msg?.role === 'assistant')
            .filter((msg) => !msg.isError && !msg.isGenerating)
            .filter((msg) => msg.content !== EMPTY_RESPONSE_FALLBACK)
            .map((msg) => ({
                role: msg.role,
                content: typeof msg.content === 'string' ? msg.content : '',
            }))
            .filter((msg) => msg.content.trim().length > 0);

        // Context switch: system prompt already reflects the new ITS problem, but
        // recency bias favors the old thread. Insert a role:user notice at the end
        // of history (before the new question) so the model sees the switch.
        const nextProblemId = this.props.problem?.id || null;
        const nextProblemTitle = this.props.problem?.title || nextProblemId || 'a new problem';
        if (
            this._lastPromptedProblemId &&
            nextProblemId &&
            this._lastPromptedProblemId !== nextProblemId
        ) {
            conversationHistory.push({
                role: 'user',
                content:
                    `We have switched to a new problem: ${nextProblemTitle}. ` +
                    `Earlier messages in this chat were about a different problem — ` +
                    `treat this as a fresh problem context going forward.`,
            });
        }
        if (nextProblemId) {
            this._lastPromptedProblemId = nextProblemId;
        }
        
        // Keep only a bounded visible window; the server owns the full transcript.
        this.setState(prevState => ({
            messages: boundChatMessages([
                ...prevState.messages,
                {
                    id: `user-${messageId}`,
                    role: 'user',
                    content: userMessage,
                    timestamp: Date.now()
                },
                {
                    id: `assistant-${messageId}`,
                    role: 'assistant',
                    content: '',
                    timestamp: Date.now(),
                    isGenerating: true
                }
            ]),
            currentMessage: '',
            isGenerating: true,
            isTyping: true
        }));

        // Get context from props
        const problemContext = this.getProblemContext();
        const turnProblemId = problemContext?.problemID || null;
        const turnStepId = problemContext?.currentStep?.id || null;
        const studentState = this.getStudentState();
        const isOfficeHours = this._isOfficeHours();
        const { text, figureUrls } = isOfficeHours
            ? { text: userMessage, figureUrls: [] }
            : this.extractConceptExplorationInput(userMessage, problemContext);
        const images = isOfficeHours ? [] : await this.fetchFiguresAsBase64(figureUrls);
        if (requestId !== this.activeRequestId) return;
        const extracted = {
            text,
            images,
            condition: this.props.condition,
            lessonId: this.props.lesson?.id,
        };

        const chatPrompt = isOfficeHours
            ? resolveFullChatPrompt(this.props.lesson)
            : (this.props.lesson?.chat_prompt || 'PROMPTv2b.txt');
        const chatDisplayMode = this.props.lesson?.chat_display_mode ?? 'Off';
        const chatPenaltyMode = this._getChatPenaltyMode();
        const chatModel = resolveChatModel(this.props.lesson);

        const assistantMessageId = `assistant-${messageId}`;
        const turnStart = Date.now();

        // Send to agent
        try {
            await agentHelper.sendMessage(
                userMessage,
                problemContext,
                studentState,
                extracted,
                chatPrompt,
                chatDisplayMode,
                chatPenaltyMode,
                {
                    onHistoryCommitted: ({ sequence }) => {
                        if (requestId !== this.activeRequestId) return;
                        this.setState((prev) => {
                            const messages = prev.messages.map((message) =>
                                message.id === `user-${messageId}` || message.id === assistantMessageId
                                    ? { ...message, sequence } : message);
                            const bounded = boundChatMessages(messages);
                            const first = bounded.find((message) => message.sequence)?.sequence;
                            return { messages: bounded, hasOlderMessages: first > 1,
                                browsingHistory: false };
                        });
                    },
                    onTurnStarted: (turnId) => {
                        const fb = this.getFirebase();
                        const sid = this.getSessionId();
                        if (fb?.logChatMessage && sid) {
                            fb.logChatMessage(sid, {
                                turnId,
                                role: 'user',
                                content: userMessage,
                                imagesCount: images.length,
                                problemId: turnProblemId,
                                stepId: turnStepId,
                                // Same hints that were sent in studentState for the prompt.
                                hintStepId: studentState?.hintStepId || turnStepId,
                                hintsUsed: Array.isArray(studentState?.hintsUsed)
                                    ? studentState.hintsUsed
                                    : [],
                                hintsUsedCount: Array.isArray(studentState?.hintsUsed)
                                    ? studentState.hintsUsed.length
                                    : 0,
                                chatPrompt,
                                chatDisplayMode,
                                chatPenaltyMode,
                                chatModel,
                                timestampMs: Date.now(),
                            });
                        }
                        if (fb?.logChatSession && sid) {
                            fb.logChatSession(sid, { messageCountUser: increment(1), lastActivityAt: Date.now() });
                        }
                    },
                    onChunkReceived: (partialResponse) => {
                        if (requestId !== this.activeRequestId) {
                            return;
                        }
                        this.setState(prevState => ({
                            messages: boundChatMessages(prevState.messages.map(msg =>
                                msg.id === assistantMessageId
                                    ? { ...msg, content: partialResponse }
                                    : msg
                            ))
                        }), () => {
                            // Follow the streaming reply to the bottom (ChatGPT-style)
                            this.scrollToBottom(true);
                        });
                    },
                    onSuccessfulCompletion: (fullResponse) => {
                        if (requestId !== this.activeRequestId) {
                            return;
                        }
                        const resolvedResponse = (fullResponse || '').trim()
                            || EMPTY_RESPONSE_FALLBACK;
                        this.setState(prevState => ({
                            messages: boundChatMessages(prevState.messages.map(msg =>
                                msg.id === assistantMessageId
                                    ? { ...msg, content: resolvedResponse, isGenerating: false }
                                    : msg
                            )),
                            isGenerating: false,
                            isTyping: false
                        }), () => {
                            this.scrollToBottom(true);
                        });
                        const fb = this.getFirebase();
                        const sid = this.getSessionId();
                        if (fb?.logChatMessage && sid) {
                            fb.logChatMessage(sid, {
                                turnId: agentHelper.getTurnId(),
                                role: 'assistant',
                                content: resolvedResponse,
                                latencyMs: Date.now() - turnStart,
                                responseCharCount: resolvedResponse.length,
                                problemId: turnProblemId,
                                stepId: turnStepId,
                                chatPrompt,
                                chatDisplayMode,
                                chatPenaltyMode,
                                chatModel,
                                timestampMs: Date.now(),
                            });
                        }
                        if (fb?.logChatSession && sid) {
                            fb.logChatSession(sid, { messageCountAssistant: increment(1), lastActivityAt: Date.now() });
                        }
                        this._maybeJudgeAgentAnswerReveal(resolvedResponse);
                    },
                    onError: (error) => {
                        if (requestId !== this.activeRequestId) {
                            return;
                        }
                        this.setState(prevState => ({
                            messages: boundChatMessages(prevState.messages.map(msg =>
                                msg.id === assistantMessageId
                                    ? { 
                                        ...msg, 
                                        content: `Sorry, I encountered an error: ${error.message}`, 
                                        isGenerating: false,
                                        isError: true 
                                    }
                                    : msg
                            )),
                            isGenerating: false,
                            isTyping: false
                        }), () => {
                            this.scrollToBottom(true);
                        });
                        const fb = this.getFirebase();
                        const sid = this.getSessionId();
                        if (fb?.logChatSession && sid) {
                            fb.logChatSession(sid, { errorCount: increment(1), lastActivityAt: Date.now() });
                        }
                    }
                },
                conversationHistory,
                chatModel
            );
        } catch (error) {
            // Error already handled in callbacks
        }
    };

    handleSuggestedQuestionClick = (question) => {
        const cleanQuestion = (question || '').trim();
        if (!cleanQuestion) return;

        if (this.props.onSuggestedQuestionClick) {
            this.props.onSuggestedQuestionClick(cleanQuestion);
            return;
        }

        if (this.state.isGenerating) {
            this.setState({ currentMessage: cleanQuestion });
            return;
        }

        this.handleSendMessage(cleanQuestion);
    };

    renderSuggestedQuestions = (questions, loadingSuggestions) => {
        const { classes } = this.props;

        if (!this._showSuggestedQuestions() || (!loadingSuggestions && questions.length === 0)) {
            return null;
        }

        return (
            <div className={classes.suggestions}>
                <div className={classes.suggestionsTitle}>
                    {loadingSuggestions ? 'Finding helpful questions...' : 'Suggested questions'}
                </div>
                {questions.length > 0 && (
                    <div className={classes.suggestionList}>
                        {questions.map((question, index) => (
                            <button
                                key={`${question}-${index}`}
                                type="button"
                                className={classes.suggestionChip}
                                onClick={() => this.handleSuggestedQuestionClick(question)}
                                disabled={loadingSuggestions}
                            >
                                {question}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    extractConceptExplorationInput(userMessage, problemContext) {
        const sources = [
            userMessage || '',
            problemContext?.problemTitle ? `Problem title: ${problemContext.problemTitle}` : '',
            problemContext?.problemBody ? `Problem body: ${problemContext.problemBody}` : '',
            problemContext?.currentStep?.title ? `Step title: ${problemContext.currentStep.title}` : '',
            problemContext?.currentStep?.body ? `Step body: ${problemContext.currentStep.body}` : '',
        ].filter(Boolean);

        const combined = sources.join('\n\n');

        // Collect figure filenames from ##filename tokens (same convention as RenderMedia).
        // Only figures from the current problem are collected; the path is identical to what
        // RenderMedia builds, so if the student can see the image the URL is resolvable.
        const figureUrls = [];
        const problemID = problemContext?.problemID;
        const imageFilenamePattern = /\.(gif|jpe?g|png|webp)$/i;
        if (problemID) {
            const figTokenRegex = /##([^\s#\n]+)/g;
            let m;
            while ((m = figTokenRegex.exec(combined)) !== null) {
                const filename = (m[1] || '').trim();
                if (filename && imageFilenamePattern.test(filename)) {
                    const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
                    figureUrls.push(
                        `${window.location.origin}${base}/static/images/figures/${CONTENT_SOURCE}/${problemID}/${filename}`
                    );
                }
            }
        }

        return {
            label: 'Concept Exploration',
            text: combined,
            figureUrls: Array.from(new Set(figureUrls)),
        };
    }

    async fetchFiguresAsBase64(figureUrls) {
        const results = [];
        for (const url of figureUrls) {
            try {
                const res = await fetch(url);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                const contentType = (res.headers.get('content-type') || '')
                    .split(';')[0]
                    .trim()
                    .toLowerCase();
                if (!VISION_IMAGE_MIME_TYPES.has(contentType)) {
                    throw new Error(`Unsupported content-type: ${contentType || 'unknown'}`);
                }

                const blob = await res.blob();
                if (!VISION_IMAGE_MIME_TYPES.has((blob.type || '').toLowerCase())) {
                    throw new Error(`Unsupported blob type: ${blob.type || 'unknown'}`);
                }

                const dataUrl = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = () => reject(new Error('read failed'));
                    reader.readAsDataURL(blob);
                });

                if (!isVisionSafeImageDataUrl(dataUrl)) {
                    throw new Error('Generated data URL is not a supported vision image');
                }

                results.push(dataUrl);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('[AI Tutor] Could not load figure for vision:', url, e);
            }
        }
        return results;
    }

    /**
     * Extract problem context for the AI agent.
     * @returns {Object} Problem context including current step, problem details, and course info
     */
    getProblemContext() {
        const { problem, lesson, seed, getActiveStepData } = this.props;
        if (this._isOfficeHours()) {
            return {
                courseName: lesson?.courseName || null,
                courseTopics: isOfficeHoursLesson(lesson)
                    ? (lesson?.courseTopics || [])
                    : collectLessonTopics(lesson),
            };
        }
        
        // Get the step student is currently working on
        const activeStepData = getActiveStepData ? getActiveStepData() : null;
        const currentStep = activeStepData ? {
            // Step docs use `id` (e.g. aaac80bvis1a), not `stepId`
            id: activeStepData.step?.id || null,
            title: activeStepData.step.stepTitle,
            body: activeStepData.step.stepBody,
            correctAnswer: activeStepData.step.correctAnswer,
            knowledgeComponents: activeStepData.step.knowledgeComponents || []
        } : null;

        return {
            problemID: problem?.id,
            problemTitle: problem?.title,
            problemBody: problem?.body,
            courseName: lesson?.courseName,
            seed: seed,
            currentStep: currentStep,
            totalSteps: problem?.steps?.length || 0
        };
    }

    /**
     * Extract student state for the AI agent.
     * @returns {Object} Student state including answers, correctness, skill mastery, and attempt history
     */
    getStudentState() {
        if (this._isOfficeHours()) {
            return {};
        }
        const { stepStates, bktParams, getActiveStepData, attemptHistory, hintUsageByStep } = this.props;
        
        // Get active step
        const activeStepData = getActiveStepData ? getActiveStepData() : null;
        const stepIndex = activeStepData ? activeStepData.stepIndex : 0;
        const isCorrect = stepStates ? stepStates[stepIndex] : null;

        // Derive hints used for the active step (manual hints only).
        // This is the same payload agent-logic formats into {hintsUsed} in the prompt.
        let hintsUsed = [];
        let hintStepId = activeStepData?.step?.id || null;
        if (hintUsageByStep && Number.isInteger(stepIndex)) {
            const usage = hintUsageByStep[stepIndex];
            if (usage?.stepId) {
                hintStepId = usage.stepId;
            }
            if (usage && Array.isArray(usage.hints)) {
                hintsUsed = usage.hints
                    .filter(h => {
                        // Only include viewed MANUAL hints:
                        // - viewed: student actually opened/used the hint
                        // - isManual flag true OR (fallback) type is not gptHint/bottomOut
                        const isManual = h.isManual !== undefined
                            ? h.isManual
                            : (h.type !== 'gptHint' && h.type !== 'bottomOut');
                        return h.viewed && isManual;
                    })
                    .map(h => ({
                        id: h.id,
                        title: h.title,
                        text: h.text,
                        type: h.type,
                        displayIndex: h.displayIndex,
                    }));
            }
        }

        // Extract skill mastery for relevant KCs
        const skillMastery = this.extractRelevantSkillMastery(
            activeStepData?.step?.knowledgeComponents,
            bktParams
        );

        // Get mastery for the current lesson
        const currentLessonMastery = this.getCurrentLessonMastery();

        return {
            isCorrect: isCorrect,
            skillMastery: skillMastery,
            attemptHistory: attemptHistory || {},
            currentLessonMastery: currentLessonMastery,
            hintsUsed,
            hintStepId,
        };
    }

    /**
     * Extract skill mastery for relevant knowledge components
     */
    extractRelevantSkillMastery(knowledgeComponents, bktParams) {
        if (!knowledgeComponents || !bktParams) {
            return {};
        }

        const relevantMastery = {};
        knowledgeComponents.forEach(kc => {
            if (bktParams[kc] && bktParams[kc].probMastery !== undefined) {
                relevantMastery[kc] = bktParams[kc].probMastery;
            }
        });

        return relevantMastery;
    }

    /**
     * Get mastery for the CURRENT lesson only (no grouping)
     * Returns array with 0 or 1 element: [{ name: "Lesson 1.1 Order of Operations", mastery: 49 }]
     */
    getCurrentLessonMastery() {
        const { lesson, lessonMasteryMap } = this.props;
        
        if (!lesson || !lesson.id || !lessonMasteryMap) {
            return [];
        }

        // Get mastery for current lesson only
        const mastery = lessonMasteryMap[lesson.id];
        
        // Only include if mastery > 0.15 (student has attempted)
        // This filters out the ~10% BKT baseline for unattempted lessons
        if (mastery && mastery > 0.15) {
            return [{
                name: `${lesson.name} ${lesson.topics}`,
                mastery: Math.round(mastery * 100)
            }];
        }
        
        return [];
    }

    renderThreadMessage = (message) => {
        const { classes } = this.props;
        const isOfficeHours = this._isOfficeHours();
        const isUser = message.role === 'user';

        if (isUser) {
            return (
                <div
                    key={message.id}
                    className={`${classes.message} ${classes.userMessage}`}
                >
                    <Paper
                        className={`${classes.messageBubble} ${classes.userBubble}${isOfficeHours ? ` ${classes.officeHoursUserBubble}` : ''}`}
                        elevation={isOfficeHours ? 0 : 1}
                    >
                        {message.content ? (
                            <Typography variant="body2" style={{ fontSize: 14, lineHeight: 1.4, fontWeight: 400 }}>
                                {message.content}
                            </Typography>
                        ) : (
                            <Typography variant="body2" style={{ fontSize: 14, lineHeight: 1.4, fontWeight: 400 }}>
                                {message.isGenerating ? 'Thinking...' : ''}
                            </Typography>
                        )}
                        {message.isGenerating && (
                            <CircularProgress size={16} style={{ marginLeft: 8 }} />
                        )}
                    </Paper>
                </div>
            );
        }

        const assistantBody = message.content ? (
            <div style={{ fontSize: 15, lineHeight: 1.6, fontWeight: isOfficeHours ? 400 : 500, color: '#1f2933' }}>
                <MessageRenderer content={message.content} />
            </div>
        ) : (
            <Typography variant="body2" style={{ fontSize: 15, lineHeight: 1.6, fontWeight: isOfficeHours ? 400 : 500, color: '#1f2933' }}>
                {message.isGenerating ? 'Thinking...' : ''}
            </Typography>
        );

        if (isOfficeHours) {
            return (
                <div
                    key={message.id}
                    className={`${classes.message} ${classes.assistantMessage} ${classes.officeHoursAssistantMessage}`}
                >
                    <div className={classes.officeHoursAssistantRow}>
                        <OskiAvatar className={classes.officeHoursAssistantAvatar} aria-hidden="true" />
                        <div className={classes.officeHoursAssistantBody}>
                            <div className={classes.officeHoursAssistantLabel}>Oski</div>
                            <div className={`${classes.assistantContent} ${classes.officeHoursAssistantText}`}>
                                {assistantBody}
                                {message.isGenerating && (
                                    <CircularProgress size={16} style={{ marginLeft: 8 }} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div
                key={message.id}
                className={`${classes.message} ${classes.assistantMessage}`}
            >
                <div className={classes.assistantContent}>
                    {assistantBody}
                    {message.isGenerating && (
                        <CircularProgress size={16} style={{ marginLeft: 8 }} />
                    )}
                </div>
            </div>
        );
    };

    renderThread = (messages, afterMessagesContent) => {
        const { classes } = this.props;
        const thread = (
            <>
                {this.state.hasOlderMessages && (
                    <Button size="small" onClick={() => this.loadHistory(false)}
                        disabled={this.state.isGenerating || this.state.isLoadingHistory}>
                        {this.state.isLoadingHistory ? 'Loading...' : 'Earlier messages'}
                    </Button>
                )}
                {this.state.browsingHistory && (
                    <Button size="small" onClick={() => this.loadHistory(true)} disabled={this.state.isLoadingHistory}>
                        Latest messages
                    </Button>
                )}
                {this.state.historyError && <Typography role="alert" color="error">{this.state.historyError}</Typography>}
                {messages.map((message) => this.renderThreadMessage(message))}
                <AnthropologyReviewStarters lesson={this.props.lesson} messages={messages}
                    disabled={this.state.isGenerating || this.state.isLoadingHistory || this.state.browsingHistory}
                    onSelect={this.handleSuggestedQuestionClick} />
                {afterMessagesContent}
                <div ref={this.messagesEndRef} />
            </>
        );

        if (this._isOfficeHours()) {
            const courseName = this.props.lesson?.courseName || 'this course';
            const lessonLabel = String(this.props.lesson?.name || '').replace(/##/g, '').trim();
            const introScope = isOfficeHoursLesson(this.props.lesson)
                ? courseName
                : (lessonLabel || courseName);
            return (
                <div className={classes.officeHoursScrollPane}>
                    <div className={classes.officeHoursColumn}>
                        <div className={classes.officeHoursIntro}>
                            <OskiAvatar className={classes.officeHoursIntroAvatar} aria-hidden="true" />
                            <h1 className={classes.officeHoursIntroTitle}>Oski · AI Tutor</h1>
                            <p className={classes.officeHoursIntroSubtitle}>
                                {courseName === 'Anthropology: Reading Review'
                                    ? 'What is Property by Pierre-Joseph Proudhon'
                                    : introScope}
                            </p>
                        </div>
                        {thread}
                    </div>
                </div>
            );
        }

        return (
            <div className={classes.chatMessages}>
                {thread}
            </div>
        );
    };

    renderComposer = (questions, loadingSuggestions) => {
        const { classes } = this.props;
        const { currentMessage, isGenerating } = this.state;
        const isOfficeHours = this._isOfficeHours();
        const chips = this.renderSuggestedQuestions(questions, loadingSuggestions);
        const field = (
            <div className={classes.inputContainer}>
                <TextField
                    className={`${classes.messageInput}${isOfficeHours ? ` ${classes.officeHoursMessageInput}` : ''}`}
                    variant="outlined"
                    size="small"
                    placeholder="Ask me anything..."
                    value={currentMessage}
                    onChange={this.handleInputChange}
                    onKeyPress={this.handleKeyPress}
                    disabled={isGenerating}
                    multiline
                    maxRows={3}
                />
                <IconButton
                    className={classes.sendButton}
                    onClick={this.handleSendMessage}
                    disabled={!currentMessage.trim() || isGenerating}
                    disableRipple
                    disableFocusRipple
                >
                    <SendArrowIcon className={classes.sendIcon} aria-label="Send" />
                </IconButton>
            </div>
        );

        const body = (
            <>
                {this.props.beforeInputContent}
                {isOfficeHours ? chips : null}
                {field}
                {isOfficeHours ? null : chips}
            </>
        );

        return (
            <div className={`${classes.chatInput}${isOfficeHours ? ` ${classes.officeHoursInput}` : ''}`}>
                {isOfficeHours ? (
                    <div className={classes.officeHoursComposerColumn}>
                        {body}
                    </div>
                ) : body}
            </div>
        );
    };

    render() {
        const { classes, responsive } = this.props;
        const isMobile = responsive?.isMobile ?? false;
        const {
            isVisible,
            messages,
            chatWidth,
            chatHeight,
            suggestedQuestions,
            isLoadingSuggestedQuestions,
        } = this.state;
        const mode = this.props.mode || 'floating';
        const allowEmbeddedClose = this.props.allowEmbeddedClose === true;
        const isChatVisible = (mode === 'embedded' && !allowEmbeddedClose) || isVisible;
        const isResizablePanel = !isMobile && (mode === 'floating' || allowEmbeddedClose);
        const useMobileSheet = isMobile && (mode === 'floating' || (mode === 'embedded' && allowEmbeddedClose));
        const isOfficeHours = this._isOfficeHours();
        const questions = this.props.suggestedQuestions || suggestedQuestions;
        const loadingSuggestions = this.props.isLoadingSuggestedQuestions ?? isLoadingSuggestedQuestions;
        const showEmbeddedHeader = mode === 'embedded' && this.props.showEmbeddedHeader !== false;
        const topContent = this.props.topContent || null;
        const afterMessagesContent = this.props.afterMessagesContent || null;
        const embeddedHeight = this.props.embeddedHeight || '100%';
        const header = (
            <div
                className={classes.chatHeader}
                {...(useMobileSheet ? { 'data-sheet-drag-handle': true } : {})}
            >
                <div className={classes.chatTitle}>
                    <OskiAvatar className={classes.avatarIcon} aria-label="Oski" />
                    <Typography variant="subtitle1">Oski • AI Tutor</Typography>
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                    {(mode !== 'embedded' || allowEmbeddedClose) && (
                        <IconButton
                            size="small"
                            onClick={this.toggleChat}
                            title="Close chat"
                            style={{ color: 'white' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    )}
                </div>
            </div>
        );

        if (useMobileSheet) {
            return (
                <>
                    {!isChatVisible && this.renderLauncher()}
                    <MobileBottomSheet
                        open={isChatVisible}
                        onClose={this.toggleChat}
                        height={MOBILE_CHAT_SHEET_HEIGHT}
                        showHeader={false}
                        handleMode="content"
                    >
                        <Card
                            ref={this.chatContainerRef}
                            className={`${classes.chatContainer} ${classes.chatContainerSheet}${isOfficeHours ? ` ${classes.officeHoursRoot}` : ''}`}
                            style={{
                                width: '100%',
                                height: '100%',
                                maxWidth: 'none',
                                maxHeight: 'none',
                                minWidth: 0,
                                minHeight: 0,
                                position: 'relative',
                                borderRadius: 0,
                                boxShadow: 'none',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {showEmbeddedHeader && header}
                            {topContent}
                            {mode === 'floating' && header}
                            <>
                                {this.renderThread(messages, afterMessagesContent)}
                                {this.renderComposer(questions, loadingSuggestions)}
                            </>
                        </Card>
                    </MobileBottomSheet>
                </>
            );
        }

        if (!isChatVisible) {
            return this.renderLauncher();
        }

        // Chat window
        const chatPanel = (
            <Card 
                ref={this.chatContainerRef}
                className={`${classes.chatContainer} ${useMobileSheet ? classes.chatContainerSheet : ''}${isOfficeHours ? ` ${classes.officeHoursRoot}` : ''}`}
                style={{
                    width: useMobileSheet
                        ? '100%'
                        : mode === 'embedded'
                        ? (allowEmbeddedClose ? chatWidth : '100%')
                        : chatWidth,
                    height: useMobileSheet
                        ? '100%'
                        : mode === 'embedded'
                        ? (allowEmbeddedClose ? chatHeight : embeddedHeight)
                        : chatHeight,
                    position: useMobileSheet
                        ? 'fixed'
                        : mode === 'embedded'
                        ? (allowEmbeddedClose ? 'fixed' : 'relative')
                        : undefined,
                    bottom: useMobileSheet
                        ? 0
                        : mode === 'embedded'
                        ? (allowEmbeddedClose ? 20 : 'auto')
                        : undefined,
                    left: useMobileSheet ? 0 : undefined,
                    right: useMobileSheet
                        ? 0
                        : mode === 'embedded'
                        ? (allowEmbeddedClose ? 20 : 'auto')
                        : undefined,
                    borderRadius: isOfficeHours
                        ? 0
                        : useMobileSheet ? '20px 20px 0 0' : (mode === 'embedded' ? 12 : undefined),
                    boxShadow: isOfficeHours
                        ? 'none'
                        : useMobileSheet
                        ? '0 -10px 40px rgba(16, 24, 40, 0.16)'
                        : (mode === 'embedded' ? '0 8px 32px rgba(0, 0, 0, 0.12)' : undefined),
                    minWidth: useMobileSheet || mode === 'embedded' ? 0 : undefined,
                    minHeight: useMobileSheet || mode === 'embedded' ? 0 : undefined,
                    maxWidth: useMobileSheet
                        ? '100vw'
                        : mode === 'embedded'
                        ? (allowEmbeddedClose ? '95vw' : 'none')
                        : undefined,
                    maxHeight: useMobileSheet
                        ? MOBILE_CHAT_SHEET_HEIGHT
                        : mode === 'embedded'
                        ? (allowEmbeddedClose ? '90vh' : 'none')
                        : undefined,
                }}
            >
                {showEmbeddedHeader && header}
                {topContent}
                {/* Resize handle */}
                {isResizablePanel && (
                    <div 
                        className={classes.resizeHandle}
                        onMouseDown={this.handleResizeStart}
                        title="Drag to resize"
                    />
                )}

                {mode === 'floating' && (
                    header
                )}

                <>
                        {this.renderThread(messages, afterMessagesContent)}
                        {this.renderComposer(questions, loadingSuggestions)}
                </>
            </Card>
        );

        return chatPanel;
    }
}

export default withStyles(styles)(withResponsive(AgentChatbox));
